import { useEffect, useRef, type ReactNode } from 'react';
import { Bar } from '@src/components/Bar/Bar';
import { Cluster } from '@src/components/Cluster/Cluster';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import { Panel } from '@src/components/Panel/Panel';
import { Snippet } from '@src/components/Snippet/Snippet';
import { Stack } from '@src/components/Stack/Stack';
import { Typography } from '@src/components/Typography/Typography';
import './FoldPanel.css';

export interface FoldPanelProps {
  id: string;
  title: string;
  hint?: string;
  example?: string;
  children: ReactNode;
}

export function openFoldPanel(id: string) {
  const details = document.getElementById(id)?.querySelector('details');
  if (details instanceof HTMLDetailsElement) {
    details.open = true;
  }
}

export function setFoldPanelsOpen(open: boolean) {
  document.querySelectorAll('.fold-panel details').forEach((node) => {
    if (node instanceof HTMLDetailsElement) {
      node.open = open;
    }
  });
}

export function FoldPanel({ id, title, hint, example, children }: FoldPanelProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (window.location.hash.slice(1) === id) {
      openFoldPanel(id);
    }
  }, [id]);

  return (
    <Panel id={id} className="fold-panel">
      <details ref={detailsRef} className="fold-panel__details">
        <summary className="fold-panel__summary">
          <Bar align="center" gap={4} wrap={false}>
            <Cluster align="baseline" gap={3}>
              <Typography as="h2" role="title/card">
                {title}
              </Typography>
              {hint && (
                <Typography as="p" role="meta/helper">
                  {hint}
                </Typography>
              )}
            </Cluster>
            <ChevronDownIcon className="fold-panel__chevron" />
          </Bar>
        </summary>
        <Stack gap={6} className="fold-panel__body">
          {children}
          {example && <Snippet>{example}</Snippet>}
        </Stack>
      </details>
    </Panel>
  );
}
